/**
 * Test-environment maintenance contract.
 *
 * One operation: put the console back to the state a QA tester needs to run the
 * same scenario again with the same phone numbers. It exists because a call
 * cannot be un-made — once a contact has a resolved call it lives in the history
 * list, and once it has asked to be removed it can never be dialled again. Both
 * are correct in production and both make a second test run impossible.
 *
 * Nothing here is available where `MAINTENANCE_RESET_ENABLED` is not explicitly
 * on, which is the only reason it is safe to expose from the browser at all.
 */
import { z } from 'zod';

/**
 * What a reset did, in the same terms the UI reports it.
 *
 * Counted rather than acknowledged with a bare 200: a tester who presses this
 * needs to know it actually found something, and "0 calls" is the difference
 * between "already clean" and "pointing at the wrong database".
 */
export const historyResetResultSchema = z.object({
  /** Resolved calls deleted, with their events and transcripts. */
  calls: z.number().int().nonnegative(),
  interactions: z.number().int().nonnegative(),
  /** Lead alerts deleted, and everyone's read marks for them. */
  alerts: z.number().int().nonnegative(),
  /** Contacts that moved back to "Contacts to call" as a result. */
  contactsReturned: z.number().int().nonnegative(),
  /** Do-not-call flags lifted, so the same number can be tested again. */
  doNotCallLifted: z.number().int().nonnegative(),
  /**
   * Calls still in flight, which were deliberately left alone. A DIALING row is
   * a concurrency slot and its webhook is still to come, so deleting it would
   * leak the slot and orphan the event.
   */
  callsInFlight: z.number().int().nonnegative(),
});
export type HistoryResetResult = z.infer<typeof historyResetResultSchema>;

/** True when nothing was there to clear — a distinct outcome, not a failure. */
export function resetWasNoOp(result: HistoryResetResult): boolean {
  return (
    result.calls === 0 &&
    result.interactions === 0 &&
    result.alerts === 0 &&
    result.doNotCallLifted === 0
  );
}

/**
 * What clearing the calling list did.
 *
 * A separate operation from the history reset, not a bigger version of it. The
 * reset puts contacts back to the top of the funnel; this one removes the people
 * entirely, so an import can be tested from an empty table.
 */
export const contactsClearResultSchema = z.object({
  contacts: z.number().int().nonnegative(),
  /** Import batch records deleted alongside them. */
  imports: z.number().int().nonnegative(),
  /**
   * Contacts left behind because a call of theirs is still in flight.
   *
   * Deleting one would cascade its DIALING row away, and that row *is* a
   * concurrency slot — losing it lets the dialler start another call while the
   * old one is still connected. Press again once the call resolves.
   */
  skippedInFlight: z.number().int().nonnegative(),
});
export type ContactsClearResult = z.infer<typeof contactsClearResultSchema>;

/** A one-line summary of a contacts clear, in the same voice as the reset's. */
export function describeContactsClear(result: ContactsClearResult): string {
  const plural = (n: number, one: string, many = `${one}s`): string =>
    `${String(n)} ${n === 1 ? one : many}`;

  if (result.contacts === 0 && result.imports === 0) {
    return result.skippedInFlight > 0
      ? `Nothing cleared — ${plural(result.skippedInFlight, 'contact')} still on a call.`
      : 'Already empty — no contacts to clear.';
  }

  const parts = [plural(result.contacts, 'contact')];
  if (result.imports > 0) parts.push(plural(result.imports, 'import record'));

  const left =
    result.skippedInFlight > 0
      ? ` ${plural(result.skippedInFlight, 'contact')} kept — still on a call.`
      : '';

  return `Deleted ${parts.join(' and ')}, with their calls and interactions.${left}`;
}

/**
 * A one-line summary of a reset, for the button to report in place.
 *
 * In the contract rather than the component so the script and the UI describe
 * the same operation the same way.
 */
export function describeReset(result: HistoryResetResult): string {
  if (resetWasNoOp(result)) return 'Already clean — nothing to clear.';

  const parts: string[] = [];
  const plural = (n: number, one: string, many = `${one}s`): string =>
    `${String(n)} ${n === 1 ? one : many}`;

  if (result.calls > 0) parts.push(plural(result.calls, 'call'));
  if (result.interactions > 0) parts.push(plural(result.interactions, 'interaction'));
  if (result.alerts > 0) parts.push(plural(result.alerts, 'alert'));
  if (result.doNotCallLifted > 0) {
    parts.push(`${plural(result.doNotCallLifted, 'do-not-call flag')} lifted`);
  }

  const cleared = `Cleared ${parts.join(', ')}.`;
  const returned =
    result.contactsReturned > 0
      ? ` ${plural(result.contactsReturned, 'contact')} back in "Contacts to call".`
      : '';
  const inFlight =
    result.callsInFlight > 0
      ? ` ${plural(result.callsInFlight, 'call')} still in flight, left alone.`
      : '';

  return cleared + returned + inFlight;
}
